import { useState, useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { articles, articleCategories, Article } from '@/mocks/articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSort, setActiveSort] = useState<'latest' | 'hot'>('latest');

  const currentCategory = useMemo(
    () => articleCategories.find((c) => c.slug === slug),
    [slug]
  );

  const [filteredArticles, setFilteredArticles] = useState<Article[]>([]);

  useEffect(() => {
    if (currentCategory) {
      let result = articles.filter((a) => a.category === currentCategory.key);
      if (activeSort === 'hot') {
        result = [...result].sort((a, b) => b.views - a.views);
      } else {
        result = [...result].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      }
      setFilteredArticles(result);
    }
  }, [currentCategory, activeSort]);

  if (!currentCategory) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-20 px-4 text-center">
          <h1 className="text-2xl font-bold text-foreground-50">分区不存在</h1>
          <Link to="/" className="text-primary-500 mt-4 inline-block">
            返回首页
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - 51黑料网`,
    description: currentCategory.description,
    url: `https://51heiliaowang.com/category/${slug}`,
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <Breadcrumb
                items={[
                  { name: '首页', path: '/' },
                  { name: currentCategory.name },
                ]}
              />
            </div>
          </div>

          {/* Category Header */}
          <section className="w-full px-4 md:px-6 pb-8">
            <div className="max-w-7xl mx-auto">
              <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                <div>
                  <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-50 mb-2">
                    {currentCategory.name}
                  </h1>
                  <p className="text-sm text-foreground-400">{currentCategory.description}</p>
                </div>
                <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 border border-background-200/50 self-start">
                  <button
                    onClick={() => setActiveSort('latest')}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                      activeSort === 'latest'
                        ? 'bg-primary-500 text-foreground-50'
                        : 'text-foreground-400 hover:text-foreground-200'
                    }`}
                  >
                    最新发布
                  </button>
                  <button
                    onClick={() => setActiveSort('hot')}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                      activeSort === 'hot'
                        ? 'bg-primary-500 text-foreground-50'
                        : 'text-foreground-400 hover:text-foreground-200'
                    }`}
                  >
                    <i className="ri-fire-line mr-1" />
                    热门排行
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* Sub-category tabs */}
          <section className="w-full px-4 md:px-6 pb-6">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center gap-2 flex-wrap">
                {articleCategories.map((cat) => (
                  <Link
                    key={cat.key}
                    to={`/category/${cat.slug}`}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                      cat.key === currentCategory.key
                        ? 'bg-accent-500/20 text-accent-400 border border-accent-500/30'
                        : 'bg-background-100 text-foreground-400 hover:text-foreground-200 border border-background-200/40 hover:border-background-300/60'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Article List */}
          <section className="w-full px-4 md:px-6 py-6">
            <div className="max-w-7xl mx-auto">
              {filteredArticles.length === 0 ? (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-100">
                    <i className="ri-inbox-line text-2xl text-foreground-500" />
                  </div>
                  <p className="text-foreground-500">该分区暂无内容</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                  {filteredArticles.map((article, index) => (
                    <Link
                      key={article.id}
                      to={`/article/${article.id}`}
                      className="group rounded-xl overflow-hidden bg-background-100 border border-background-200/40 hover:border-primary-500/30 transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                    >
                      <div className="relative aspect-[16/9] overflow-hidden">
                        <img
                          src={article.image}
                          alt={article.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        {index < 3 && activeSort === 'hot' && (
                          <div className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center rounded-lg bg-accent-500 text-foreground-50 font-heading text-sm font-bold">
                            {index + 1}
                          </div>
                        )}
                      </div>
                      <div className="p-4">
                        <h3 className="font-medium text-foreground-100 text-sm md:text-base line-clamp-2 mb-2 group-hover:text-primary-500 transition-colors leading-snug">
                          {article.title}
                        </h3>
                        <p className="text-xs text-foreground-400 line-clamp-2 mb-3 leading-relaxed">
                          {article.excerpt}
                        </p>
                        <div className="flex items-center justify-between text-xs text-foreground-500">
                          <span>{article.date}</span>
                          <span className="flex items-center gap-1">
                            <i className="ri-eye-line" /> {article.views.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}